import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Star1, Flag, Profile2User, Component, Calendar2, Add, ArchiveBox, Refresh2 } from "iconsax-react";
import { Button, Select, DatePicker } from "@/shared/ui";
import type { RangeValue } from "@/shared/ui";
import type { TaskListItem, TaskPriority, Assignee } from "../../../types";
import { PRIORITY_MAP } from "../../../consts";

interface BoardFilterBarProps {
  kpiFilter: number | null;
  onKpiChange: (value: number | null) => void;
  kpiTasks: TaskListItem[];
  priorityFilter: TaskPriority | null;
  onPriorityChange: (value: TaskPriority | null) => void;
  assigneeFilter: number | null;
  onAssigneeChange: (value: number | null) => void;
  assignees: Assignee[];
  favoritesOnly: boolean;
  onFavoritesToggle: (value: boolean) => void;
  kpiFlagOnly: boolean;
  onKpiFlagToggle: (value: boolean) => void;
  onCreateTask?: () => void;
  canCreate: boolean;
  dueDateRange?: RangeValue | null;
  onDueDateChange?: (value: RangeValue | null) => void;
  isBacklogActive?: boolean;
  onBacklogToggle?: (value: boolean) => void;
  hasActiveFilter?: boolean;
  onResetFilters?: () => void;
}

export default function BoardFilterBar({
  kpiFilter,
  onKpiChange,
  kpiTasks,
  priorityFilter,
  onPriorityChange,
  assigneeFilter,
  onAssigneeChange,
  assignees,
  favoritesOnly,
  onFavoritesToggle,
  kpiFlagOnly,
  onKpiFlagToggle,
  onCreateTask,
  canCreate,
  dueDateRange,
  onDueDateChange,
  isBacklogActive,
  onBacklogToggle,
  hasActiveFilter,
  onResetFilters,
}: BoardFilterBarProps) {
  const { t, i18n } = useTranslation("BoardPage");
  const { t: tTasks } = useTranslation("TasksPage");
  const locale = i18n.language as "ru" | "kk" | "en";

  const kpiOptions = useMemo<{ value: number; label: string }[]>(
    () => kpiTasks.map((k) => ({ value: k.id, label: k.title })),
    [kpiTasks],
  );

  const priorityOptions = (["low", "normal", "high", "critical"] as TaskPriority[]).map((p) => ({
    value: p as string,
    label: tTasks(PRIORITY_MAP[p].label),
  }));

  const assigneeOptions = assignees.map((a) => ({
    value: String(a.id),
    label: a.name,
  }));

  return (
    <div className="flex items-center justify-between gap-2 mb-7">
      <div className="flex justify-start items-center gap-2">
        {onBacklogToggle && (
          <Button
            variant={isBacklogActive ? "primary" : "secondary"}
            className="w-8 h-8 flex justify-center items-center p-1! rounded-md!"
            onClick={() => onBacklogToggle(!isBacklogActive)}>
            <ArchiveBox size={16} color="currentColor" variant={isBacklogActive ? "Bold" : "Linear"} />
          </Button>
        )}
        <div className="grid grid-cols-4 gap-2">
          <Select
            value={assigneeFilter !== null ? String(assigneeFilter) : null}
            onChange={(val) => onAssigneeChange(val ? Number(val) : null)}
            options={assigneeOptions}
            placeholder={t("filters.assignee")}
            isSmall
            icon={<Profile2User size={16} color="currentColor" />}
          />
          <Select
            value={priorityFilter}
            onChange={(val) => onPriorityChange(val as TaskPriority | null)}
            options={priorityOptions}
            placeholder={t("filters.priority")}
            isSmall
            icon={<Component size={16} color="currentColor" />}
          />
          {onDueDateChange && (
            <DatePicker
              mode="range"
              locale={locale}
              value={dueDateRange}
              onChange={onDueDateChange}
              placeholder={t("filters.dueDate")}
              isSmall
              icon={<Calendar2 size={16} color="currentColor" />}
            />
          )}
          <Select<number>
            value={kpiFilter}
            onChange={onKpiChange}
            options={kpiOptions}
            placeholder={t("filters.goal")}
            isSmall
            icon={<Flag size={16} color="currentColor" />}
            clearable
          />
        </div>
        <Button
          variant="secondary"
          className="w-8 h-8 flex justify-center items-center p-1! rounded-md!"
          onClick={() => onFavoritesToggle(!favoritesOnly)}>
          <span className={favoritesOnly ? "viz-categorical-01-text" : "content-base-secondary"}>
            <Star1 size={16} color="currentColor" variant={favoritesOnly ? "Bold" : "Linear"} />
          </span>
        </Button>

        <Button
          variant="secondary"
          className="w-8 h-8 flex justify-center items-center p-1! rounded-md!"
          onClick={() => onKpiFlagToggle(!kpiFlagOnly)}>
          <span className={kpiFlagOnly ? "viz-categorical-06-text" : "content-base-secondary"}>
            <Flag size={16} color="currentColor" variant={kpiFlagOnly ? "Bold" : "Linear"} />
          </span>
        </Button>

        {hasActiveFilter && onResetFilters && (
          <button
            className="px-3 h-8 flex justify-center items-center gap-2 content-base-low cursor-pointer"
            onClick={onResetFilters}>
            <Refresh2 size={16} color="currentColor" />
            <span className="text-label-sm">{t("filters.reset")}</span>
          </button>
        )}
      </div>

      <div className="flex items-center justify-end gap-3">
        {canCreate && onCreateTask && (
          <Button variant="primary" size="md" onClick={onCreateTask}>
            <Add size={16} color="currentColor" />
            {t("buttons.createTask")}
          </Button>
        )}
      </div>
    </div>
  );
}
